import {
  ControlledDropdown,
  ControlledDropdownWithInput,
} from '../../../../components/form-fields';
import { HeadingSubform } from './HeadingSubform';
import getConstants from '../../../../util/constants';
import { TextWithTooltip, styled } from '@payright/web-components';
import { DeepMap, FieldError } from 'react-hook-form';
import { FormHouseholdExpensesFormData } from './FormHouseholdExpenses';
import { SubFormContent, SubFormFields } from './SubFormStyles';
import { useDropdownOptions } from '@payright/web-components';
import { useEffect } from 'react';

const { RELATIONSHIP_STATUS, DEPENDANTS_DROPDOWN, INCOME_PERCENTAGES } = getConstants();

const RelationshipStatusTooltipContent = "What's your relationship status?";

const DependantsTooltipContent =
  'How many people depend on you financially? This might include children, relatives or spouses.';

const HouseholdIncomeTooltipContent = (
  <>
    Household Income includes wages & salary plus any other income such as Centrelink,
    superannuation and investments received by you and your partner.
  </>
);

const SCTooltipContent = styled.div`
  .underline {
    text-decoration: underline;
  }

  .bold {
    font-weight: 700;
  }
`;

type SubformHouseholdSituationProps = {
  errors: DeepMap<FormHouseholdExpensesFormData['householdDetails'], FieldError>;
  watch: any;
  setValue: any;
  unregister: any;
};

export const SubformHouseholdSituation = ({
  errors,
  watch,
  setValue,
  unregister,
}: SubformHouseholdSituationProps) => {
  const relationshipStatus = watch('householdDetails.relationshipStatus');

  const [incomePercentages, temporarilyAddAnotherOption] = useDropdownOptions(
    INCOME_PERCENTAGES,
    '%'
  );

  const handleIncomePercentageChange = (value: any) => {
    const percentage = typeof value === 'string' ? parseFloat(value) : value;
    temporarilyAddAnotherOption(percentage);
    setValue('householdDetails.appliedPercentage', `${value}%`);
    setValue('householdDetails.incomePercentage', `${value}`);
  };

  useEffect(() => {
    if (relationshipStatus === 'Single') {
      unregister('householdDetails.incomePercentage');
      unregister('householdDetails.appliedPercentage');
    }
  }, [relationshipStatus, unregister]);

  return (
    <SubFormContent>
      <HeadingSubform>Household Situation</HeadingSubform>

      <SubFormFields>
        <ControlledDropdown
          placeholder="Please select"
          className="md:col-start-1"
          name="householdDetails.relationshipStatus"
          rules={{ required: 'Relationship Status is required' }}
          error={errors.relationshipStatus?.message}
          options={RELATIONSHIP_STATUS}
        >
          <div className="label-with-tooltip">
            Relationship Status
            <TextWithTooltip content={RelationshipStatusTooltipContent}>
              What's this?
            </TextWithTooltip>
          </div>
        </ControlledDropdown>
        <ControlledDropdown
          placeholder="Please select"
          rules={{
            required: 'Dependants is required',
          }}
          error={errors.dependants?.message}
          name="householdDetails.dependants"
          options={DEPENDANTS_DROPDOWN}
        >
          <div className="label-with-tooltip">
            Dependants
            <TextWithTooltip content={DependantsTooltipContent}>What's this?</TextWithTooltip>
          </div>
        </ControlledDropdown>
        {relationshipStatus !== 'Single' && (
          <ControlledDropdownWithInput
            dropdownName="householdDetails.appliedPercentage"
            inputName="householdDetails.incomePercentage"
            className="grid-all-column"
            options={incomePercentages}
            inputSuffix="%"
            inputRules={{
              required: 'Your share of household income is required',
              min: {
                value: 0,
                message: 'Please enter a number between 0 & 100',
              },
              max: {
                value: 100,
                message: 'Please enter a number between 0 & 100',
              },
              validate: (value: any) => {
                if (isNaN(value)) {
                  return 'Your share of household income must be a number';
                }
              },
            }}
            error={errors?.incomePercentage?.message}
            onInputChange={handleIncomePercentageChange}
            onDropdownChange={handleIncomePercentageChange}
          >
            <div className="label-with-tooltip">
              <span>
                <span className="underline">Your share</span> of Household Income
              </span>
              <div className="label-with-tooltip">
                <TextWithTooltip content={HouseholdIncomeTooltipContent}>
                  What's this?
                </TextWithTooltip>
              </div>
              <span className="label-secondary">
                What portion of your household's income comes from you?
              </span>
            </div>
          </ControlledDropdownWithInput>
        )}
      </SubFormFields>
    </SubFormContent>
  );
};
